/* eslint-disable eslint-comments/no-restricted-disable -- These issues are known and will be fixed in a future PR */
import { Link, useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type {
  MarketingEmailTemplate,
  MarketingEmailTemplateContentType,
} from "@/react/types/MarketingEmailTemplates";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { templateLocaleNamespace } from "../../UpsertTemplateModal/UpdateTemplateContent";
import { useIsSystemTemplatesEnabled } from "../../VisualEmailBuilder/useIsSystemTemplatesEnabled";
import { CardOptions } from "../CardOptions";
import { useDeleteTemplate } from "../hooks/useDeleteTemplate";
import { useRenameTemplate } from "../hooks/useRenameTemplate";
import {
  getResponsiveCardClasses,
  useTemplateCardSize,
} from "../hooks/useTemplateCardSize";
import { ScaledTemplatePreview } from "./ScaledTemplatePreview";

export const TemplateCardV2 = ({
  template,
  onApply,
}: {
  template: MarketingEmailTemplate;
  onApply: (
    templateId: number,
    contentType: MarketingEmailTemplateContentType,
  ) => void;
}) => {
  const history = useHistory();
  const { dimensions } = useTemplateCardSize();
  const cardClasses = getResponsiveCardClasses();
  const templateTypeLocaleKey =
    template.content_type === "tip_tap_email" ? "text" : "visual";

  const { deleteModalElement, openDeleteModal } = useDeleteTemplate({
    template,
  });

  const { renameTemplateElement, openRenameTemplateModal } = useRenameTemplate({
    template,
  });

  const isSystemTemplatesEnabled = useIsSystemTemplatesEnabled();

  const shouldShowOptions =
    template.template_type === "user" ||
    (template.template_type === "system" && isSystemTemplatesEnabled);

  const isTemplateTypeVisual = template.content_type === "visual_email_builder";
  const isTemplateTypeText = template.content_type === "tip_tap_email";

  return (
    <>
      <Link
        to={{
          pathname: `/templates/${template.id}`,
          state: {
            template,
          },
        }}
        className="group flex flex-col gap-3"
      >
        <div
          className={classNames(
            "relative overflow-hidden rounded-[0.5rem]",
            cardClasses.width,
            cardClasses.height,
            "after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:transition-colors",
            "after:duration-300 group-hover:after:bg-black/80",
          )}
        >
          <ScaledTemplatePreview
            template={template}
            containerWidth={dimensions.width}
          />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- This is not an interactive component. It just needs to block click events from propagating upwards. Does not take any action */}
          <div
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {shouldShowOptions && (
              <div className="absolute right-3 top-3 z-30 hidden group-hover:block">
                <CardOptions
                  openDeleteModal={openDeleteModal}
                  openRenameTemplateModal={openRenameTemplateModal}
                />
              </div>
            )}
            <div className="absolute inset-0 z-20 hidden items-center justify-center group-hover:flex">
              <div className="flex w-full flex-col gap-y-3 px-12">
                <Button
                  variant="secondary"
                  type="button"
                  large
                  full
                  onClick={() => onApply(template.id, template.content_type)}
                  className="hover:!bg-tertiary !text-dark !bg-white"
                >
                  {t([templateLocaleNamespace, "apply"])}
                </Button>
                <Button
                  variant="secondary"
                  large
                  full
                  type="button"
                  className="border-transparent !bg-transparent !text-white hover:!bg-white/10"
                  onClick={() => {
                    history.push({
                      pathname: `/templates/${template.id}`,
                      state: {
                        template,
                      },
                    });
                  }}
                >
                  {t([templateLocaleNamespace, "preview"])}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <Typography.LabelSm weight="semibold" truncate>
            {template.name}
          </Typography.LabelSm>
          <div
            className={classNames(
              "flex w-fit items-center gap-2 rounded-md px-1.5 py-px",
              {
                "bg-tertiary": isTemplateTypeText,
                "bg-[#3B82F6]/10": isTemplateTypeVisual,
              },
            )}
          >
            <Typography.LabelXs
              weight="medium"
              color={isTemplateTypeVisual ? "text-link" : ""}
            >
              {t([templateLocaleNamespace, templateTypeLocaleKey])}
            </Typography.LabelXs>
          </div>
        </div>
      </Link>
      {deleteModalElement}
      {renameTemplateElement}
    </>
  );
};
